import { Tooltip, useDisclosure } from '@chakra-ui/react';
import { IconSettingsGear2 } from 'central-icons/IconSettingsGear2';

import { IconButton } from '@revelio/ui';

import { useEditMode } from '../../components/EditModeProvider/EditModeProvider';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import styles from './EditFiltersButton.module.css';
import { FilterSelectModal } from './FilterSelectModal';

interface EditFiltersButtonProps {
  /**
   * When provided, the button calls this on click and does not render the modal.
   * Use when the button is inside a popover so the parent can render the modal
   * outside the popover and keep it mounted when the popover closes.
   */
  onOpenModal?: () => void;
}

/**
 * EditFiltersButton
 *
 * A button that opens a modal to configure which filters are available on the page.
 * Only renders when:
 * 1. Edit mode is enabled
 * 2. The page is a custom page (no revelioPageId)
 *
 * Core Revelio pages (with revelioPageId) are not affected.
 */
export const EditFiltersButton = ({
  onOpenModal,
}: EditFiltersButtonProps = {}) => {
  const { editMode } = useEditMode();
  const { config, revelioPageId, dispatch } = useTabContentConfig();
  const internalDisclosure = useDisclosure();

  // When used inside popover, parent controls modal; otherwise we own it
  const isControlledByParent = onOpenModal != null;
  const onOpen = isControlledByParent ? onOpenModal : internalDisclosure.onOpen;
  const modalIsOpen = isControlledByParent
    ? undefined
    : internalDisclosure.isOpen;
  const modalOnClose = isControlledByParent
    ? undefined
    : internalDisclosure.onClose;

  // Only show on custom pages (no revelioPageId) in edit mode
  const isCustomPage = !revelioPageId;
  if (!editMode || !isCustomPage) {
    return null;
  }

  const handleSave = (filters: SecondaryFilterConfig[]) => {
    dispatch({
      type: 'SET_SECONDARY_FILTERS',
      payload: { filters },
    });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <>
      <Tooltip label="Manage available filters" placement="top" hasArrow>
        <IconButton
          aria-label="Manage filters"
          icon={<IconSettingsGear2 size="16px" />}
          variant="ghost"
          size="md"
          className={styles.editFiltersButton}
          onClick={handleClick}
          data-testid="edit-filters-button"
        />
      </Tooltip>
      {!isControlledByParent && (
        <FilterSelectModal
          isOpen={modalIsOpen ?? false}
          onClose={modalOnClose ?? (() => undefined)}
          currentFilters={config.secondaryFilters}
          onSave={handleSave}
        />
      )}
    </>
  );
};
